import React, { createContext, useContext, useState, useEffect } from "react";
import { ApiService } from "../services/api";

export type BackgroundPattern = "grid" | "dots" | "lines" | "waves" | "circles" | "none";

export interface Theme {
  id: string;
  name: string;
  colors: {
    primary: string;
    secondary: string;
    accent: string;
    background: {
      base: string;
      via: string;
      to: string;
    };
    orbs: {
      orb1: string;
      orb2: string;
      orb3: string;
    };
  };
}

export const backgroundPatterns = [
  { id: "grid", name: "Izgara", description: "Klasik grid deseni" },
  { id: "dots", name: "Noktalar", description: "Nokta deseni" },
  { id: "lines", name: "Çizgiler", description: "Diagonal çizgiler" },
  { id: "waves", name: "Dalgalar", description: "Dalga efekti" },
  { id: "circles", name: "Halkalar", description: "Halka deseni" },
  { id: "none", name: "Yok", description: "Sadece düz arka plan" },
];

export const themes: Theme[] = [
  {
    id: "default",
    name: "Mor Galaksi",
    colors: {
      primary: "#4f46e5",
      secondary: "#8b5cf6",
      accent: "#a78bfa",
      background: {
        base: "#0f0a24",
        via: "#1a1340",
        to: "#0f0a24",
      },
      orbs: {
        orb1: "rgba(79, 70, 229, 0.03)",
        orb2: "rgba(139, 92, 246, 0.03)",
        orb3: "rgba(99, 102, 241, 0.03)",
      },
    },
  },
  {
    id: "ocean",
    name: "Mavi Okyanus",
    colors: {
      primary: "#0ea5e9",
      secondary: "#06b6d4",
      accent: "#22d3ee",
      background: {
        base: "#06101c",
        via: "#0d1f38",
        to: "#06101c",
      },
      orbs: {
        orb1: "rgba(14, 165, 233, 0.03)",
        orb2: "rgba(6, 182, 212, 0.03)",
        orb3: "rgba(34, 211, 238, 0.03)",
      },
    },
  },
  {
    id: "forest",
    name: "Yeşil Orman",
    colors: {
      primary: "#10b981",
      secondary: "#14b8a6",
      accent: "#34d399",
      background: {
        base: "#05140c",
        via: "#0d2818",
        to: "#05140c",
      },
      orbs: {
        orb1: "rgba(16, 185, 129, 0.03)",
        orb2: "rgba(20, 184, 166, 0.03)",
        orb3: "rgba(52, 211, 153, 0.03)",
      },
    },
  },
  {
    id: "sunset",
    name: "Gün Batımı",
    colors: {
      primary: "#f97316",
      secondary: "#fb923c",
      accent: "#fdba74",
      background: {
        base: "#1a0c08",
        via: "#2d1810",
        to: "#1a0c08",
      },
      orbs: {
        orb1: "rgba(249, 115, 22, 0.03)",
        orb2: "rgba(251, 146, 60, 0.03)",
        orb3: "rgba(253, 186, 116, 0.03)",
      },
    },
  },
  {
    id: "energy",
    name: "Kırmızı Enerji",
    colors: {
      primary: "#ef4444",
      secondary: "#f43f5e",
      accent: "#fb7185",
      background: {
        base: "#1a080a",
        via: "#2d0f15",
        to: "#1a080a",
      },
      orbs: {
        orb1: "rgba(239, 68, 68, 0.03)",
        orb2: "rgba(244, 63, 94, 0.03)",
        orb3: "rgba(251, 113, 133, 0.03)",
      },
    },
  },
  {
    id: "midnight",
    name: "Gece Mavisi",
    colors: {
      primary: "#3b82f6",
      secondary: "#6366f1",
      accent: "#818cf8",
      background: {
        base: "#080c16",
        via: "#0f1729",
        to: "#080c16",
      },
      orbs: {
        orb1: "rgba(59, 130, 246, 0.03)",
        orb2: "rgba(99, 102, 241, 0.03)",
        orb3: "rgba(129, 140, 248, 0.03)",
      },
    },
  },
];

interface ThemeContextType {
  currentTheme: Theme;
  setTheme: (themeId: string) => void;
  themes: Theme[];
  backgroundPattern: BackgroundPattern;
  setBackgroundPattern: (pattern: BackgroundPattern) => void;
  isAnimated: boolean;
  setIsAnimated: (animated: boolean) => void;
}

const ThemeContext = createContext<ThemeContextType | undefined>(undefined);

export const ThemeProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const initialPrefs = (() => {
    try {
      const raw = localStorage.getItem("userPreferences");
      return raw ? JSON.parse(raw) : null;
    } catch (_) {
      return null;
    }
  })();

  const initialThemeIndex = initialPrefs && typeof initialPrefs.themeType === "number" ? initialPrefs.themeType : 0;
  const initialPatternIndex = initialPrefs && typeof initialPrefs.bgPatternType === "number" ? initialPrefs.bgPatternType : 0;
  const initialAnimated = initialPrefs && typeof initialPrefs.bgAnimated === "boolean" ? initialPrefs.bgAnimated : true;

  const patternMap: BackgroundPattern[] = ["grid", "dots", "lines", "waves", "circles", "none"];

  const [currentTheme, setCurrentTheme] = useState<Theme>(themes[initialThemeIndex] || themes[0]);
  const [backgroundPattern, setBackgroundPatternState] = useState<BackgroundPattern>(patternMap[initialPatternIndex] || "grid");
  const [isAnimated, setIsAnimatedState] = useState<boolean>(initialAnimated);

  useEffect(() => {
    try {
      const raw = localStorage.getItem("userPreferences");
      if (raw) {
        const prefs = JSON.parse(raw);
        if (typeof prefs?.showSeconds === "boolean") {
          window.dispatchEvent(new CustomEvent("timeFormatChanged", { detail: prefs.showSeconds }));
        }
      }
    } catch (_) {}
  }, []);

  const loadUserThemePreferences = async () => {
    try {
      const token = localStorage.getItem("authToken");
      if (!token) {
        setCurrentTheme(themes[0]);
        setBackgroundPatternState("grid");
        setIsAnimatedState(true);
        return;
      }
      const me = await ApiService.getMe();
      const prefs = me?.preferences || {};

      const themeIndex = typeof prefs.themeType === "number" ? prefs.themeType : 0;
      const themeByIndex = themes[themeIndex] || themes[0];
      setCurrentTheme(themeByIndex);

      const patterns: BackgroundPattern[] = ["grid", "dots", "lines", "waves", "circles", "none"];
      const patternIndex = typeof prefs.bgPatternType === "number" ? prefs.bgPatternType : 0;
      const patternValue = patterns[patternIndex] || "grid";
      setBackgroundPatternState(patternValue);

      const animated = typeof prefs.bgAnimated === "boolean" ? prefs.bgAnimated : true;
      setIsAnimatedState(animated);

      if (typeof prefs.showSeconds === "boolean") {
        window.dispatchEvent(new CustomEvent("timeFormatChanged", { detail: prefs.showSeconds }));
      }
    } catch (e) {
      console.error("Error loading user theme preferences:", e);
    }
  };

  useEffect(() => {
    loadUserThemePreferences();
  }, []);

  useEffect(() => {
    const handleAuthChange = () => {
      loadUserThemePreferences();
    };

    window.addEventListener("userLoggedIn", handleAuthChange);
    window.addEventListener("userLoggedOut", handleAuthChange);

    return () => {
      window.removeEventListener("userLoggedIn", handleAuthChange);
      window.removeEventListener("userLoggedOut", handleAuthChange);
    };
  }, []);

  useEffect(() => {
    const root = document.documentElement;
    root.style.setProperty("--theme-primary", currentTheme.colors.primary);
    root.style.setProperty("--theme-secondary", currentTheme.colors.secondary);
    root.style.setProperty("--theme-accent", currentTheme.colors.accent);
    root.style.setProperty("--theme-bg-base", currentTheme.colors.background.base);
    root.style.setProperty("--theme-bg-via", currentTheme.colors.background.via);
    root.style.setProperty("--theme-bg-to", currentTheme.colors.background.to);
  }, [currentTheme]);

  const setTheme = (themeId: string) => {
    const theme = themes.find((t) => t.id === themeId);
    if (theme) {
      setCurrentTheme(theme);
      const index = Math.max(
        0,
        themes.findIndex((t) => t.id === themeId),
      );

      ApiService.updateMyPreferences({ themeType: index }).catch(() => {});
      window.dispatchEvent(new CustomEvent("themeChanged", { detail: theme }));
    }
  };

  const setBackgroundPattern = (pattern: BackgroundPattern) => {
    setBackgroundPatternState(pattern);
    const patterns: BackgroundPattern[] = ["grid", "dots", "lines", "waves", "circles", "none"];
    const index = Math.max(0, patterns.indexOf(pattern));
    ApiService.updateMyPreferences({ bgPatternType: index }).catch(() => {});
    window.dispatchEvent(new CustomEvent("backgroundPatternChanged", { detail: pattern }));
  };

  const setIsAnimated = (animated: boolean) => {
    setIsAnimatedState(animated);
    ApiService.updateMyPreferences({ bgAnimated: animated }).catch(() => {});
    window.dispatchEvent(new CustomEvent("backgroundAnimationChanged", { detail: animated }));
  };

  return (
    <ThemeContext.Provider
      value={{
        currentTheme,
        setTheme,
        themes,
        backgroundPattern,
        setBackgroundPattern,
        isAnimated,
        setIsAnimated,
      }}>
      {children}
    </ThemeContext.Provider>
  );
};

export const useTheme = () => {
  const context = useContext(ThemeContext);
  if (context === undefined) {
    throw new Error("useTheme must be used within a ThemeProvider");
  }
  return context;
};
